import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withKeys as _withKeys, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "title-text" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "fields" }
const _hoisted_4 = {
  key: 0,
  class: "oldest"
}
const _hoisted_5 = { class: "title-text" }
const _hoisted_6 = { class: "fields" }

import { computed, ref, onMounted, watch } from 'vue';

import NcTextField from '@nextcloud/vue/components/NcTextField';

import { t } from '@services/l10n';
import * as utils from '@services/utils/common';

import type { IPhoto } from '@typings';


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'EditDate',
},
  __name: 'EditDate',
  props: {
    photos: {},
    disabled: { type: Boolean }
  },
  emits: ["save"],
  setup(__props: any, { expose: __expose, emit: __emit }) {



const props = __props;

const emit = __emit;

const sortedPhotos = ref<IPhoto[]>([]);

const year = ref('0');
const month = ref('0');
const day = ref('0');
const hour = ref('0');
const minute = ref('0');
const second = ref('0');

const yearLast = ref('0');
const monthLast = ref('0');
const dayLast = ref('0');
const hourLast = ref('0');
const minuteLast = ref('0');
const secondLast = ref('0');

const newestDirty = ref(false);
const oldestDirty = ref(false);

const date = computed(() => makeDate(year.value, month.value, day.value, hour.value, minute.value, second.value));

const dateLast = computed(() =>
  makeDate(yearLast.value, monthLast.value, dayLast.value, hourLast.value, minuteLast.value, secondLast.value),
);

const dateDiff = computed(() => (date.value && dateLast.value ? date.value.getTime() - dateLast.value.getTime() : 0));

const origDateNewest = computed(() => new Date(sortedPhotos.value[0].datetaken! * 1000));
const origDateOldest = computed(() => new Date(sortedPhotos.value.at(-1)!.datetaken! * 1000));
const origDateDiff = computed(() => origDateNewest.value.getTime() - origDateOldest.value.getTime());
const scaleFactor = computed(() => (origDateDiff.value > 0 ? dateDiff.value / origDateDiff.value : 0));

const longDateStr = computed(() =>
  date.value ? utils.getLongDateStr(date.value, false, true) : t('memories', 'Invalid Date'),
);

const longDateStrLast = computed(() =>
  dateLast.value ? utils.getLongDateStr(dateLast.value, false, true) : t('memories', 'Invalid Date'),
);

onMounted(() => {
  init();
});

watch(
  () => props.photos,
  () => {
    init();
  },
);

function init() {
  // Filter out only photos that have a datetaken
  const photos = (sortedPhotos.value = props.photos.filter((photo) => photo.datetaken !== undefined));

  // Sort photos by datetaken descending
  photos.sort((a, b) => b.datetaken! - a.datetaken!);

  // Get date of newest photo
  let date = new Date(photos[0].datetaken! * 1000);
  year.value = date.getUTCFullYear().toString();
  month.value = (date.getUTCMonth() + 1).toString();
  day.value = date.getUTCDate().toString();
  hour.value = date.getUTCHours().toString();
  minute.value = date.getUTCMinutes().toString();
  second.value = date.getUTCSeconds().toString();

  // Get date of oldest photo
  if (photos.length > 1) {
    date = new Date(photos.at(-1)!.datetaken! * 1000);
    yearLast.value = date.getUTCFullYear().toString();
    monthLast.value = (date.getUTCMonth() + 1).toString();
    dayLast.value = date.getUTCDate().toString();
    hourLast.value = date.getUTCHours().toString();
    minuteLast.value = date.getUTCMinutes().toString();
    secondLast.value = date.getUTCSeconds().toString();
  }
}

function validate() {
  if (!date.value) {
    throw new Error(t('memories', 'Invalid Date'));
  }

  if (props.photos.length > 1) {
    if (!dateLast.value) {
      throw new Error(t('memories', 'Invalid Date'));
    }

    if (dateDiff.value < -60000) {
      // 1 minute
      throw new Error(t('memories', 'Newest date is older than oldest date'));
    }
  }
}

function result(photo: IPhoto): undefined | string {
  if (!oldestDirty.value && !newestDirty.value) {
    return undefined;
  }

  if (sortedPhotos.value.length === 0 || !date.value) {
    return undefined;
  }

  if (sortedPhotos.value.length === 1) {
    return utils.getExifDateStr(date.value);
  }

  // Interpolate date
  const dT = date.value.getTime();
  const doT = origDateNewest.value.getTime();
  const offset = ((photo.datetaken ?? 0) * 1000 || doT) - doT;
  return utils.getExifDateStr(new Date(dT + offset * scaleFactor.value));
}

function newestChange(time = false) {
  if (sortedPhotos.value.length === 0 || !date.value) {
    return;
  }

  newestDirty.value = true;

  // Set the last date to have the same offset to newest date
  try {
    const dateNew = date.value;
    const offset = dateNew.getTime() - origDateNewest.value.getTime();
    const dateLastNew = new Date(origDateOldest.value.getTime() + offset);

    yearLast.value = dateLastNew.getUTCFullYear().toString();
    monthLast.value = (dateLastNew.getUTCMonth() + 1).toString();
    dayLast.value = dateLastNew.getUTCDate().toString();

    if (time) {
      hourLast.value = dateLastNew.getUTCHours().toString();
      minuteLast.value = dateLastNew.getUTCMinutes().toString();
      secondLast.value = dateLastNew.getUTCSeconds().toString();
    }
  } catch (error) {}
}

function oldestChange() {
  oldestDirty.value = true;
}

function makeDate(yearS: string, monthS: string, dayS: string, hourS: string, minuteS: string, secondS: string) {
  const year = parseInt(yearS, 10);
  const month = parseInt(monthS, 10) - 1;
  const day = parseInt(dayS, 10);
  const hour = parseInt(hourS, 10);
  const minute = parseInt(minuteS, 10);
  let second = parseInt(secondS, 10) || 0; // needs validation

  if (isNaN(year)) return null;
  if (isNaN(month)) return null;
  if (isNaN(day)) return null;
  if (isNaN(hour)) return null;
  if (isNaN(minute)) return null;
  if (isNaN(second)) return null;

  // Validate date
  if (year < 0 || year > 5000) return null;
  if (month < 0 || month > 11) return null;

  // Number of days in month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;

  // Validate time
  if (hour < 0 || hour > 23) return null;
  if (minute < 0 || minute > 59) return null;
  if (second < 0 || second > 59) second = 0;

  return new Date(Date.UTC(year, month, day, hour, minute, second));
}

__expose({ validate, result });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      (__props.photos.length > 1)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, " [" + _toDisplayString(_unref(t)('memories', 'Newest')) + "] ", 1))
        : _createCommentVNode("", true),
      _createTextVNode(" " + _toDisplayString(longDateStr.value) + " " + _toDisplayString(newestDirty.value ? '*' : ''), 1)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(NcTextField), {
        class: "field",
        type: "number",
        min: "0",
        max: "5000",
        modelValue: year.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((year).value = $event)),
        label: _unref(t)('memories', 'Year'),
        "label-visible": true,
        placeholder: _unref(t)('memories', 'Year'),
        disabled: __props.disabled,
        onInput: _cache[1] || (_cache[1] = ($event: any) => (newestChange())),
        onKeypress: _cache[2] || (_cache[2] = _withKeys(($event: any) => (emit('save')), ["enter"]))
      }, null, 8, ["modelValue", "label", "placeholder", "disabled"]),
      _createVNode(_unref(NcTextField), {
        class: "field",
        type: "number",
        min: "1",
        max: "12",
        modelValue: month.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((month).value = $event)),
        label: _unref(t)('memories', 'Month'),
        "label-visible": true,
        placeholder: _unref(t)('memories', 'Month'),
        disabled: __props.disabled,
        onInput: _cache[4] || (_cache[4] = ($event: any) => (newestChange())),
        onKeypress: _cache[5] || (_cache[5] = _withKeys(($event: any) => (emit('save')), ["enter"]))
      }, null, 8, ["modelValue", "label", "placeholder", "disabled"]),
      _createVNode(_unref(NcTextField), {
        class: "field",
        type: "number",
        min: "1",
        max: "31",
        modelValue: day.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((day).value = $event)),
        label: _unref(t)('memories', 'Day'),
        "label-visible": true,
        placeholder: _unref(t)('memories', 'Day'),
        disabled: __props.disabled,
        onInput: _cache[7] || (_cache[7] = ($event: any) => (newestChange())),
        onKeypress: _cache[8] || (_cache[8] = _withKeys(($event: any) => (emit('save')), ["enter"]))
      }, null, 8, ["modelValue", "label", "placeholder", "disabled"]),
      _createVNode(_unref(NcTextField), {
        class: "field",
        type: "number",
        min: "0",
        max: "23",
        modelValue: hour.value,
        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((hour).value = $event)),
        label: _unref(t)('memories', 'Hour'),
        "label-visible": true,
        placeholder: _unref(t)('memories', 'Hour'),
        disabled: __props.disabled,
        onInput: _cache[10] || (_cache[10] = ($event: any) => (newestChange(true))),
        onKeypress: _cache[11] || (_cache[11] = _withKeys(($event: any) => (emit('save')), ["enter"]))
      }, null, 8, ["modelValue", "label", "placeholder", "disabled"]),
      _createVNode(_unref(NcTextField), {
        class: "field",
        type: "number",
        min: "0",
        max: "59",
        modelValue: minute.value,
        "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((minute).value = $event)),
        label: _unref(t)('memories', 'Minute'),
        placeholder: _unref(t)('memories', 'Minute'),
        disabled: __props.disabled,
        onInput: _cache[13] || (_cache[13] = ($event: any) => (newestChange(true))),
        onKeypress: _cache[14] || (_cache[14] = _withKeys(($event: any) => (emit('save')), ["enter"]))
      }, null, 8, ["modelValue", "label", "placeholder", "disabled"])
    ]),
    (__props.photos.length > 1)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("span", null, " [" + _toDisplayString(_unref(t)('memories', 'Oldest')) + "] ", 1),
            _createTextVNode(" " + _toDisplayString(longDateStrLast.value) + " " + _toDisplayString(oldestDirty.value ? '*' : ''), 1)
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(NcTextField), {
              class: "field",
              type: "number",
              min: "0",
              max: "5000",
              modelValue: yearLast.value,
              "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((yearLast).value = $event)),
              label: _unref(t)('memories', 'Year'),
              "label-visible": true,
              placeholder: _unref(t)('memories', 'Year'),
              disabled: __props.disabled,
              onInput: _cache[16] || (_cache[16] = ($event: any) => (oldestChange())),
              onKeypress: _cache[17] || (_cache[17] = _withKeys(($event: any) => (emit('save')), ["enter"]))
            }, null, 8, ["modelValue", "label", "placeholder", "disabled"]),
            _createVNode(_unref(NcTextField), {
              class: "field",
              type: "number",
              min: "1",
              max: "12",
              modelValue: monthLast.value,
              "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((monthLast).value = $event)),
              label: _unref(t)('memories', 'Month'),
              "label-visible": true,
              placeholder: _unref(t)('memories', 'Month'),
              disabled: __props.disabled,
              onInput: _cache[19] || (_cache[19] = ($event: any) => (oldestChange())),
              onKeypress: _cache[20] || (_cache[20] = _withKeys(($event: any) => (emit('save')), ["enter"]))
            }, null, 8, ["modelValue", "label", "placeholder", "disabled"]),
            _createVNode(_unref(NcTextField), {
              class: "field",
              type: "number",
              min: "1",
              max: "31",
              modelValue: dayLast.value,
              "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((dayLast).value = $event)),
              label: _unref(t)('memories', 'Day'),
              "label-visible": true,
              placeholder: _unref(t)('memories', 'Day'),
              disabled: __props.disabled,
              onInput: _cache[22] || (_cache[22] = ($event: any) => (oldestChange())),
              onKeypress: _cache[23] || (_cache[23] = _withKeys(($event: any) => (emit('save')), ["enter"]))
            }, null, 8, ["modelValue", "label", "placeholder", "disabled"]),
            _createVNode(_unref(NcTextField), {
              class: "field",
              type: "number",
              min: "0",
              max: "23",
              modelValue: hourLast.value,
              "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((hourLast).value = $event)),
              label: _unref(t)('memories', 'Hour'),
              "label-visible": true,
              placeholder: _unref(t)('memories', 'Hour'),
              disabled: __props.disabled,
              onInput: _cache[25] || (_cache[25] = ($event: any) => (oldestChange())),
              onKeypress: _cache[26] || (_cache[26] = _withKeys(($event: any) => (emit('save')), ["enter"]))
            }, null, 8, ["modelValue", "label", "placeholder", "disabled"]),
            _createVNode(_unref(NcTextField), {
              class: "field",
              type: "number",
              min: "0",
              max: "59",
              modelValue: minuteLast.value,
              "onUpdate:modelValue": _cache[27] || (_cache[27] = ($event: any) => ((minuteLast).value = $event)),
              label: _unref(t)('memories', 'Minute'),
              placeholder: _unref(t)('memories', 'Minute'),
              disabled: __props.disabled,
              onInput: _cache[28] || (_cache[28] = ($event: any) => (oldestChange())),
              onKeypress: _cache[29] || (_cache[29] = _withKeys(($event: any) => (emit('save')), ["enter"]))
            }, null, 8, ["modelValue", "label", "placeholder", "disabled"])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})